import { getPostalCodeGeocode } from "api/services/common";
import { ICities } from "types/Common";

export type ParsedPostalCodeLocation = {
  cityName?: string;
  stateName?: string;
  stateShortName?: string;
};

type StateOption = {
  label: string;
  value: number | string;
  code?: string;
};

export function getGeocodeCountryFilter(countryName: string, googleCountryCode?: string): string {
  // Google accepts ISO 3166-1 alpha-2 codes or country names. Our API also exposes a
  // 3-letter iso_code (e.g. IND) which Google rejects; prefer the 2-letter code field.
  if (googleCountryCode?.length === 2) {
    return googleCountryCode;
  }

  return countryName;
}

export function findMatchingState(
  stateName: string,
  statesOptions: StateOption[],
  stateShortName?: string,
): StateOption | undefined {
  const normalizedStateName = stateName.trim().toLowerCase();
  const normalizedStateShortName = stateShortName?.trim().toLowerCase();
  const normalizedOptions = statesOptions.map((option) => ({
    option,
    label: option.label.trim().toLowerCase(),
    code: option.code?.trim().toLowerCase(),
  }));

  const exactMatch =
    normalizedOptions.find(({ label }) => label === normalizedStateName) ??
    normalizedOptions.find(({ code }) => code === normalizedStateName) ??
    (normalizedStateShortName
      ? normalizedOptions.find(({ label, code }) => code === normalizedStateShortName || label === normalizedStateShortName)
      : undefined);

  if (exactMatch) {
    return exactMatch.option;
  }

  // Google and our API label some regions differently ("Wilayah Persekutuan Kuala Lumpur" vs
  // "Kuala Lumpur"), so containment is the last resort. Longest label wins, otherwise Google's
  // "West Virginia" would match the "Virginia" option.
  return [...normalizedOptions]
    .sort((a, b) => b.label.length - a.label.length)
    .find(({ label }) => label.includes(normalizedStateName) || normalizedStateName.includes(label))?.option;
}

export function findMatchingCity(cityName: string, cities: ICities[]): ICities | undefined {
  const normalizedCityName = cityName.trim().toLowerCase();

  return cities.find(
    ({ name, name_wo_diacritics }) =>
      name.trim().toLowerCase() === normalizedCityName || name_wo_diacritics?.trim().toLowerCase() === normalizedCityName,
  );
}

export function isAbortError(error: unknown): boolean {
  if (typeof error === "object" && error !== null && "code" in error && (error as { code?: string }).code === "ERR_CANCELED") {
    return true;
  }

  return error instanceof Error && (error.name === "AbortError" || error.name === "CanceledError");
}

export async function geocodePostalCode(
  zipcode: string,
  countryFilter: string,
  signal?: AbortSignal,
): Promise<ParsedPostalCodeLocation | null> {
  try {
    const { data } = await getPostalCodeGeocode({
      params: {
        zipcode: zipcode.trim(),
        country: countryFilter,
      },
      signal,
    });

    if (!data?.city_name && !data?.state_name) {
      return null;
    }

    return {
      cityName: data.city_name ?? undefined,
      stateName: data.state_name ?? undefined,
      stateShortName: data.state_short_name ?? undefined,
    };
  } catch (error) {
    if (isAbortError(error)) {
      return null;
    }

    console.warn("Postal code geocode lookup failed");
    return null;
  }
}

type SetFormValue = (name: string, value: unknown, options?: { shouldDirty?: boolean; shouldValidate?: boolean }) => void;

type ApplyZipcodeLookupParams = {
  zipcode: string;
  countryName: string;
  googleCountryCode?: string;
  countryHasStatesOrProvinces: boolean;
  customCityFieldName?: string;
  allowCustomCity?: boolean;
  setValue: SetFormValue;
  fieldNames: {
    stateId: string;
    stateName: string;
    cityId: string;
    cityName: string;
  };
  fetchStates: () => Promise<StateOption[]>;
  fetchCities: (stateId?: number) => Promise<ICities[]>;
  signal?: AbortSignal;
};

type CityWithState = ICities & { state_id?: number };

type FormUpdate = [name: string, value: unknown];

/**
 * Resolves a postal code to Cargobase state/city ids and writes them to the form.
 * Returns whether any value was actually applied, so callers can tell a successful
 * auto-fill apart from a lookup that found nothing.
 */
export async function applyZipcodeLookup({
  zipcode,
  countryName,
  googleCountryCode,
  countryHasStatesOrProvinces,
  customCityFieldName = "custom_city_name",
  allowCustomCity = false,
  setValue,
  fieldNames,
  fetchStates,
  fetchCities,
  signal,
}: ApplyZipcodeLookupParams): Promise<boolean> {
  const countryFilter = getGeocodeCountryFilter(countryName, googleCountryCode);
  const parsedLocation = await geocodePostalCode(zipcode, countryFilter, signal);
  if (!parsedLocation) {
    return false;
  }

  const updates: FormUpdate[] = [];
  let statesForMatching: StateOption[] = [];
  let matchedStateId: number | undefined;

  if (countryHasStatesOrProvinces) {
    statesForMatching = await fetchStates();

    if (parsedLocation.stateName) {
      const matchedState = findMatchingState(parsedLocation.stateName, statesForMatching, parsedLocation.stateShortName);
      if (matchedState) {
        matchedStateId = Number(matchedState.value);
        updates.push([fieldNames.stateId, matchedStateId], [fieldNames.stateName, matchedState.label]);
      }
    }
  }

  if (parsedLocation.cityName) {
    const cities = await fetchCities(matchedStateId);
    const matchedCity = findMatchingCity(parsedLocation.cityName, cities) as CityWithState | undefined;

    if (matchedCity) {
      updates.push([fieldNames.cityId, matchedCity.id], [fieldNames.cityName, matchedCity.name]);
      if (allowCustomCity) {
        updates.push([customCityFieldName, null]);
      }

      if (!matchedStateId && matchedCity.state_id && countryHasStatesOrProvinces) {
        const stateFromCity = statesForMatching.find((state) => Number(state.value) === Number(matchedCity.state_id));
        if (stateFromCity) {
          updates.push([fieldNames.stateId, Number(stateFromCity.value)], [fieldNames.stateName, stateFromCity.label]);
        }
      }
    } else if (allowCustomCity) {
      updates.push(
        [fieldNames.cityId, parsedLocation.cityName],
        [fieldNames.cityName, parsedLocation.cityName],
        [customCityFieldName, parsedLocation.cityName],
      );
    }
  }

  // Applied in one go at the very end so a lookup that was superseded by a newer one
  // cannot write a partial address.
  if (!updates.length || signal?.aborted) {
    return false;
  }

  updates.forEach(([name, value]) => setValue(name, value, { shouldDirty: true, shouldValidate: true }));

  return true;
}
